import React, { useState } from 'react';
import {
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { COLORS } from '../config/colors';
import { FONTS } from '../config/fonts';
import { useIsHorizontal } from '../hooks/useIsHorizontal';
import { useStationSearch } from '../hooks/useStationSearch';
import { strings } from '../strings';
import type { Station } from '../types/station';
import SearchIcon from './icons/SearchIcon';

type SearchOverlayProps = {
  stations: Station[];
  onSelectStation: (station: Station) => void;
  onClose: () => void;
};

const SearchOverlay = ({ stations, onSelectStation, onClose }: SearchOverlayProps) => {
  const insets = useSafeAreaInsets();
  const isHorizontal = useIsHorizontal();
  const [query, setQuery] = useState('');
  const results = useStationSearch(stations, query);

  const handleSelectStation = (station: Station) => {
    Keyboard.dismiss();
    onSelectStation(station);
    onClose();
  };

  const renderItem = ({ item }: { item: Station }) => (
    <Pressable
      accessibilityLabel={`${item.name}، ${item.city}`}
      accessibilityRole="button"
      onPress={() => handleSelectStation(item)}
      style={({ pressed }) => [styles.resultItem, pressed && styles.resultItemPressed]}
    >
      <View style={styles.resultDot} />
      <View style={styles.resultTextContainer}>
        <Text style={styles.resultName}>{item.name}</Text>
        <Text style={styles.resultCity}>{item.city}</Text>
      </View>
    </Pressable>
  );

  const renderEmpty = () => (
    <View accessibilityLiveRegion="polite" style={styles.emptyState}>
      <Text style={styles.emptyText}>
        {query.trim().length > 0 ? strings.noStationsFound : strings.startTypingToSearch}
      </Text>
    </View>
  );

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      // Horizontal mode turns the full-screen sheet into a left-docked panel.
      style={[
        styles.overlay,
        isHorizontal ? styles.overlayHorizontal : styles.overlayFull,
      ]}
    >
      <View style={[styles.header, { paddingTop: insets.top }]}>
        <View style={styles.searchBar}>
          <SearchIcon />
          <TextInput
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus
            accessibilityLabel={strings.searchStations}
            accessibilityHint={strings.searchInputHint}
            placeholder={strings.searchStationsOrCities}
            placeholderTextColor="#9ca3af"
            returnKeyType="search"
            style={styles.input}
            value={query}
            onChangeText={setQuery}
          />
          {query.length > 0 ? (
            <Pressable
              accessibilityLabel={strings.clearSearch}
              accessibilityRole="button"
              hitSlop={8}
              onPress={() => setQuery('')}
            >
              <Text style={styles.clearText}>✕</Text>
            </Pressable>
          ) : null}
        </View>
        <Pressable
          accessibilityLabel={strings.closeSearch}
          accessibilityRole="button"
          hitSlop={8}
          onPress={() => {
            Keyboard.dismiss();
            onClose();
          }}
        >
          <Text style={styles.cancelText}>{strings.cancel}</Text>
        </Pressable>
      </View>

      <FlatList
        contentContainerStyle={styles.resultsContent}
        data={results}
        keyboardShouldPersistTaps="handled"
        keyExtractor={item => item.name}
        renderItem={renderItem}
        ListEmptyComponent={renderEmpty}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  overlay: {
    direction: 'rtl',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: '#ffffff',
  },
  overlayFull: {
    right: 0,
  },
  overlayHorizontal: {
    width: '100%',
    maxWidth: 420,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#f3f4f6',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  input: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: '#111827',
    padding: 0,
  },
  clearText: {
    fontFamily: FONTS.regular,
    fontSize: 18,
    color: '#9ca3af',
    paddingHorizontal: 4,
  },
  cancelText: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.primary,
  },
  resultsContent: {
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e5e7eb',
  },
  resultItemPressed: {
    backgroundColor: '#f3f4f6',
  },
  resultDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#d1495b',
  },
  resultTextContainer: {
    flex: 1,
  },
  resultName: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  resultCity: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    fontFamily: FONTS.regular,
    fontSize: 15,
    color: '#9ca3af',
  },
});

export default SearchOverlay;